// BYO-01: "Your own Claude Code login" — a reference to the person's own logged-in vendor CLI, used
// for their own crew only. No token is asked for or stored: the form has no secret field at all.
// While the path is off (the default, OPEN-QUESTIONS 46–47) the card explains why instead.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Button,
  Checkbox,
  Field,
  FieldLabel,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SettingsGroup,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TerminalIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../settings/problem-text";
import { ANTHROPIC_TERMS_URL, COPY } from "./copy";
import { subscriptionKeys } from "./queries";

const CLI_PROVIDERS = [
  { value: "anthropic", label: "Anthropic (Claude Code)" },
  { value: "openai", label: "OpenAI (Codex CLI)" },
] as const;

export function CliCard({
  api,
  co,
  open,
  canManage,
}: {
  api: Api;
  co: string;
  open: boolean;
  canManage: boolean;
}) {
  return (
    <SettingsGroup title={COPY.cliTitle} data-slot="cli-subscription">
      <div className="flex flex-col gap-3 p-4">
        {open ? (
          <CliForm api={api} co={co} />
        ) : (
          <Notice tone="info" title="Not available yet" data-testid="cli-off">
            <p>{COPY.cliOffWhy}</p>
            <p className="mt-2">
              <a
                className="underline underline-offset-2"
                href={ANTHROPIC_TERMS_URL}
                target="_blank"
                rel="noreferrer"
              >
                Anthropic's terms for Claude Code
              </a>
              {canManage ? " · Admins turn it on in Team › Connections › Paths once allowed." : ""}
            </p>
          </Notice>
        )}
        <p className="text-xs text-muted-foreground">{COPY.pooledRefused}</p>
      </div>
    </SettingsGroup>
  );
}

function CliForm({ api, co }: { api: Api; co: string }) {
  const qc = useQueryClient();
  const ids = { provider: useId(), plan: useId(), where: useId(), own: useId(), terms: useId() };
  const [provider, setProvider] = useState<string>("anthropic");
  const [plan, setPlan] = useState("");
  const [where, setWhere] = useState<"user_device" | "runtime_session">("user_device");
  const [own, setOwn] = useState(false);
  const [terms, setTerms] = useState(false);
  const add = useMutation({
    mutationFn: () =>
      api.client.modelCredentials.create({
        params: { co },
        idempotencyKey: newIdempotencyKey(),
        body: {
          kind: "cli_subscription",
          scope: "person",
          provider,
          label: provider === "anthropic" ? "My Claude Code login" : "My CLI login",
          plan: plan.trim(),
          login_location: where,
          attestation: {
            terms_ref: ANTHROPIC_TERMS_URL,
            plan_is_own: own,
            own_work_only: true,
            allows_paid_client_work: false,
          },
        },
      }),
    onSuccess: () => {
      setOwn(false);
      setTerms(false);
      void qc.invalidateQueries({ queryKey: subscriptionKeys.credentials(co) });
      toast.add({ title: "CLI login recorded (no token stored)", type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not recorded."), type: "error" }),
  });
  const ready = plan.trim() && own && terms;
  return (
    <form
      className="flex flex-col gap-4"
      aria-label="Record your own CLI login"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) add.mutate();
      }}
    >
      <div className="grid gap-4 md:grid-cols-3">
        <Field>
          <FieldLabel htmlFor={ids.provider}>CLI</FieldLabel>
          <NativeSelect
            id={ids.provider}
            className="w-full"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          >
            {CLI_PROVIDERS.map((p) => (
              <NativeSelectOption key={p.value} value={p.value}>
                {p.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <Field>
          <FieldLabel htmlFor={ids.plan}>Your plan</FieldLabel>
          <Input
            id={ids.plan}
            placeholder="Max"
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={ids.where}>Where it is logged in</FieldLabel>
          <NativeSelect
            id={ids.where}
            className="w-full"
            value={where}
            onChange={(e) => setWhere(e.target.value as typeof where)}
          >
            <NativeSelectOption value="user_device">My own device</NativeSelectOption>
            <NativeSelectOption value="runtime_session">My own runtime session</NativeSelectOption>
          </NativeSelect>
        </Field>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-start gap-2">
          <Checkbox id={ids.own} checked={own} onCheckedChange={(v) => setOwn(v === true)} />
          <Label htmlFor={ids.own} className="font-normal leading-snug">
            The plan is mine, logged in by me, and runs only my own crew.
          </Label>
        </div>
        <div className="flex items-start gap-2">
          <Checkbox id={ids.terms} checked={terms} onCheckedChange={(v) => setTerms(v === true)} />
          <Label htmlFor={ids.terms} className="font-normal leading-snug">
            I have read the vendor's terms for its CLI and my plan.
          </Label>
        </div>
      </div>
      <div>
        <Button type="submit" size="sm" disabled={!ready || add.isPending}>
          <TerminalIcon data-icon="inline-start" aria-hidden="true" />
          Record my CLI login
        </Button>
      </div>
    </form>
  );
}
